import React, { useState, useEffect, useRef } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Globe, Search, ChevronDown, X, ArrowUpCircle } from 'lucide-react';

// Đối tượng dịch thuật để hỗ trợ đa ngôn ngữ
const translations = {
  'vi': {
    appTitle: 'AI dự đoán Giá Coin',
    selectLanguageLabel: 'Chọn Ngôn ngữ:',
    selectCoinLabel: 'Chọn Đồng Coin:',
    searchCoinPlaceholder: 'Tìm kiếm coin...',
    predictButtonText: 'Phân tích & Dự đoán',
    loadingButtonText: 'Đang phân tích...',
    loadingMessage: 'Đang tải...',
    loadingData: 'Đang tải dữ liệu lịch sử...',
    loadingCoins: 'Đang tải danh sách coin...',
    readyForPrediction: 'Sẵn sàng để dự đoán.',
    dataLoadingError: 'Không thể tải dữ liệu. Vui lòng thử lại sau.',
    noDataForPrediction: 'Không có dữ liệu lịch sử để phân tích. Vui lòng tải lại.',
    notEnoughData: 'Không đủ dữ liệu (cần ít nhất 7 ngày) để dự đoán xu hướng.',
    predictionTitle: 'Kết quả Dự đoán:',
    predictionStronglyUp: 'Giá có xu hướng TĂNG MẠNH trong thời gian tới.',
    predictionSlightlyUp: 'Giá có xu hướng TĂNG nhẹ trong thời gian tới.',
    predictionStronglyDown: 'Giá có xu hướng GIẢM MẠNH trong thời gian tới.',
    predictionSlightlyDown: 'Giá có xu hướng GIẢM nhẹ trong thời gian tới.',
    predictionStable: 'Giá có xu hướng ỔN ĐỊNH hoặc biến động nhẹ.',
    predictionText: (price) => `Giá dự kiến vào ngày mai là ${price}`,
    chartTitle: 'Biểu đồ Giá Lịch sử & Dự đoán:',
    tableTitle: 'Dữ liệu Giá Lịch sử (Chi tiết):',
    tableDateHeader: 'Ngày',
    tablePriceHeader: 'Giá (USD)',
    predictionLabel: '(Dự đoán)',
    priceLabel: 'Giá',
    dateLabel: 'Ngày',
    noPrediction: 'Chưa có dự đoán nào.',
    noCoinsFound: 'Không tìm thấy coin nào.',
    disclaimerTitle: 'Tuyên bố miễn trừ trách nhiệm',
    disclaimerContent: [
      'Mô hình dự đoán giá tiền điện tử này chỉ được cung cấp cho mục đích thông tin và giáo dục. Nó không phải là một hình thức tư vấn tài chính, tư vấn đầu tư hoặc khuyến nghị giao dịch. Bất kỳ quyết định đầu tư nào bạn đưa ra dựa trên thông tin này đều hoàn toàn do bạn chịu trách nhiệm.',
      'Thị trường tiền điện tử rất biến động và rủi ro. Giá có thể thay đổi nhanh chóng và không thể dự đoán trước. Mọi khoản đầu tư đều có rủi ro mất vốn.',
      'Mặc dù mô hình này được xây dựng dựa trên dữ liệu lịch sử và các phương pháp thống kê, nhưng độ chính xác của các dự đoán không được đảm bảo. Kết quả trong quá khứ không phải là chỉ báo đáng tin cậy cho kết quả trong tương lai.',
      'Bạn nên tìm kiếm lời khuyên từ chuyên gia tài chính trước khi đưa ra bất kỳ quyết định đầu tư nào.'
    ]
  },
  'en': {
    appTitle: 'AI Coin Price Prediction',
    selectLanguageLabel: 'Select Language:',
    selectCoinLabel: 'Select Coin:',
    searchCoinPlaceholder: 'Search for a coin...',
    predictButtonText: 'Analyze & Predict',
    loadingButtonText: 'Analyzing...',
    loadingMessage: 'Loading...',
    loadingData: 'Fetching historical data...',
    loadingCoins: 'Fetching coin list...',
    readyForPrediction: 'Ready for prediction.',
    dataLoadingError: 'Could not load data. Please try again later.',
    noDataForPrediction: 'No historical data to analyze. Please reload.',
    notEnoughData: 'Not enough data (at least 7 days) to predict the trend.',
    predictionTitle: 'Prediction Result:',
    predictionStronglyUp: 'The price has a strong UPWARD trend in the near future.',
    predictionSlightlyUp: 'The price has a slight UPWARD trend in the near future.',
    predictionStronglyDown: 'The price has a strong DOWNWARD trend in the near future.',
    predictionSlightlyDown: 'The price has a slight DOWNWARD trend in the near future.',
    predictionStable: 'The price has a stable or slight fluctuation trend.',
    predictionText: (price) => `The predicted price for tomorrow is ${price}`,
    chartTitle: 'Historical Price & Prediction Chart:',
    tableTitle: 'Historical Price Data (Details):',
    tableDateHeader: 'Date',
    tablePriceHeader: 'Price (USD)',
    predictionLabel: '(Prediction)',
    priceLabel: 'Price',
    dateLabel: 'Date',
    noPrediction: 'No prediction has been made yet.',
    noCoinsFound: 'No coins found.',
    disclaimerTitle: 'Disclaimer',
    disclaimerContent: [
      'This cryptocurrency price prediction model is provided for informational and educational purposes only. It is not a form of financial advice, investment advice, or a trading recommendation. Any investment decisions you make based on this information are at your own risk.',
      'The cryptocurrency market is highly volatile and risky. Prices can change rapidly and are unpredictable. All investments carry the risk of capital loss.',
      'Although this model is built on historical data and statistical methods, the accuracy of its predictions is not guaranteed. Past results are not a reliable indicator of future performance.',
      'You should seek advice from a financial professional before making any investment decisions.'
    ]
  },
  'zh': {
    appTitle: 'AI加密货币价格预测',
    selectLanguageLabel: '选择语言:',
    selectCoinLabel: '选择硬币:',
    searchCoinPlaceholder: '搜索硬币...',
    predictButtonText: '分析和预测',
    loadingButtonText: '正在分析...',
    loadingMessage: '正在加载...',
    loadingData: '正在获取历史数据...',
    loadingCoins: '正在获取硬币列表...',
    readyForPrediction: '准备好进行预测。',
    dataLoadingError: '无法加载数据。请稍后再试。',
    noDataForPrediction: '没有可分析的历史数据。请重新加载。',
    notEnoughData: '数据不足（至少需要7天）来预测趋势。',
    predictionTitle: '预测结果:',
    predictionStronglyUp: '近期价格有强劲的上涨趋势。',
    predictionSlightlyUp: '近期价格有轻微的上涨趋势。',
    predictionStronglyDown: '近期价格有强劲的下跌趋势。',
    predictionSlightlyDown: '近期价格有轻微的下跌趋势。',
    predictionStable: '价格趋势稳定或波动不大。',
    predictionText: (price) => `明天的预计价格是 ${price}`,
    chartTitle: '历史价格与预测图表:',
    tableTitle: '历史价格数据 (详细):',
    tableDateHeader: '日期',
    tablePriceHeader: '价格 (USD)',
    predictionLabel: '(预测)',
    priceLabel: '价格',
    dateLabel: '日期',
    noPrediction: '尚未进行任何预测。',
    noCoinsFound: '找不到硬币。',
    disclaimerTitle: '免责声明',
    disclaimerContent: [
      '此加密货币价格预测模型仅供参考和教育目的。它不构成财务建议、投资建议或交易推荐。您根据此信息做出的任何投资决定，风险自负。',
      '加密货币市场波动性大且风险高。价格可能会迅速变化且不可预测。所有投资均有资本损失的风险。',
      '尽管此模型是基于历史数据和统计方法构建的，但其预测的准确性无法保证。过去的表现并非未来结果的可靠指标。',
      '在做出任何投资决定之前，您应该寻求专业金融人士的建议。'
    ]
  },
  'ja': {
    appTitle: 'AI仮想通貨価格予測',
    selectLanguageLabel: '言語を選択:',
    selectCoinLabel: 'コインを選択:',
    searchCoinPlaceholder: 'コインを検索...',
    predictButtonText: '分析と予測',
    loadingButtonText: '分析中...',
    loadingMessage: '読み込み中...',
    loadingData: '履歴データを取得中...',
    loadingCoins: 'コインリストを取得中...',
    readyForPrediction: '予測の準備ができました。',
    dataLoadingError: 'データを読み込めませんでした。後でもう一度お試しください。',
    noDataForPrediction: '分析する履歴データがありません。再読み込みしてください。',
    notEnoughData: 'トレンドを予測するにはデータが不十分です（少なくとも7日必要です）。',
    predictionTitle: '予測結果:',
    predictionStronglyUp: '価格は近い将来、強い上昇傾向にあります。',
    predictionSlightlyUp: '価格は近い将来、わずかに上昇傾向にあります。',
    predictionStronglyDown: '価格は近い将来、強い下落傾向にあります。',
    predictionSlightlyDown: '価格は近い将来、わずかに下落傾向にあります。',
    predictionStable: '価格は安定しているか、わずかな変動傾向にあります。',
    predictionText: (price) => `明日の予想価格は ${price} です`,
    chartTitle: '履歴価格と予測チャート:',
    tableTitle: '履歴価格データ (詳細):',
    tableDateHeader: '日付',
    tablePriceHeader: '価格 (USD)',
    predictionLabel: '(予測)',
    priceLabel: '価格',
    dateLabel: '日付',
    noPrediction: 'まだ予測は行われていません。',
    noCoinsFound: 'コインが見つかりませんでした。',
    disclaimerTitle: '免責事項',
    disclaimerContent: [
      'この仮想通貨価格予測モデルは、情報提供および教育目的でのみ提供されています。これは、金融アドバイス、投資アドバイス、または取引推奨を構成するものではありません。この情報に基づいて行ういかなる投資決定も、ご自身の責任で行ってください。',
      '仮想通貨市場は非常に変動が大きく、リスクがあります。価格は急激に変化し、予測不可能です。すべての投資には資本損失のリスクが伴います。',
      'このモデルは履歴データと統計的手法に基づいて構築されていますが、その予測の正確性は保証されません。過去の結果は将来のパフォーマンスの信頼できる指標ではありません。',
      '投資判断を下す前に、金融の専門家のアドバイスを求める必要があります。'
    ]
  },
  'ko': {
    appTitle: 'AI 코인 가격 예측',
    selectLanguageLabel: '언어 선택:',
    selectCoinLabel: '코인 선택:',
    searchCoinPlaceholder: '코인 검색...',
    predictButtonText: '분석 및 예측',
    loadingButtonText: '분석 중...',
    loadingMessage: '로딩 중...',
    loadingData: '과거 데이터 가져오는 중...',
    loadingCoins: '코인 목록 가져오는 중...',
    readyForPrediction: '예측 준비 완료.',
    dataLoadingError: '데이터를 로드할 수 없습니다. 나중에 다시 시도해주세요.',
    noDataForPrediction: '분석할 과거 데이터가 없습니다. 다시 로드해주세요.',
    notEnoughData: '추세를 예측하기에 데이터가 충분하지 않습니다 (최소 7일 필요).',
    predictionTitle: '예측 결과:',
    predictionStronglyUp: '가격은 가까운 시일 내에 강한 상승 추세를 보입니다.',
    predictionSlightlyUp: '가격은 가까운 시일 내에 약간의 상승 추세를 보입니다.',
    predictionStronglyDown: '가격은 가까운 시일 내에 강한 하락 추세를 보입니다.',
    predictionSlightlyDown: '가격은 가까운 시일 내에 약간의 하락 추세를 보입니다.',
    predictionStable: '가격은 안정적이거나 약간의 변동 추세를 보입니다.',
    predictionText: (price) => `내일 예상 가격은 ${price} 입니다`,
    chartTitle: '과거 가격 및 예측 차트:',
    tableTitle: '과거 가격 데이터 (상세):',
    tableDateHeader: '날짜',
    tablePriceHeader: '가격 (USD)',
    predictionLabel: '(예측)',
    priceLabel: '가격',
    dateLabel: '날짜',
    noPrediction: '아직 예측이 이루어지지 않았습니다.',
    noCoinsFound: '코인을 찾을 수 없습니다.',
    disclaimerTitle: '면책 조항',
    disclaimerContent: [
      '이 암호화폐 가격 예측 모델은 정보 제공 및 교육 목적으로만 제공됩니다. 이는 재정 조언, 투자 조언 또는 거래 권장 사항이 아닙니다. 이 정보에 근거하여 내리는 모든 투자 결정은 전적으로 귀하의 책임입니다.',
      '암호화폐 시장은 변동성이 매우 크고 위험합니다. 가격은 빠르게 변할 수 있으며 예측할 수 없습니다. 모든 투자에는 자본 손실의 위험이 수반됩니다.',
      '이 모델은 과거 데이터와 통계적 방법을 기반으로 구축되었지만, 예측의 정확성은 보장되지 않습니다. 과거의 결과는 미래 성과에 대한 신뢰할 수 있는 지표가 아닙니다.',
      '투자 결정을 내리기 전에 금융 전문가의 조언을 구해야 합니다.'
    ]
  },
};

// Hàm trợ giúp để định dạng giá, giữ nhiều số thập phân cho các giá trị nhỏ
const formatPrice = (price) => {
  if (price === 0) return '$0.00';
  if (price < 1) {
    // Hiển thị 8 chữ số thập phân cho các giá trị < 1 USD, loại bỏ các số 0 ở cuối
    return `$${parseFloat(price.toFixed(8))}`;
  }
  return `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Custom hook để debouncing
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  const timeoutRef = useRef();

  useEffect(() => {
    // Xóa timeout trước đó nếu có
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Đặt một timeout mới
    timeoutRef.current = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    // Hàm dọn dẹp để xóa timeout khi component bị unmount hoặc giá trị thay đổi
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [value, delay]);

  return debouncedValue;
};

// Hàm để fetch với chiến lược backoff lũy thừa
const fetchWithRetry = async (url, options, retries = 3) => {
  for (let i = 0; i < retries; i++) {
    try {
      const response = await fetch(url, options);
      if (response.ok) {
        return response;
      }
      console.error(`Fetch attempt ${i + 1} failed with status: ${response.status}`);
    } catch (error) {
      console.error(`Fetch attempt ${i + 1} failed:`, error);
      if (i === retries - 1) throw error; // Ném lại lỗi ở lần thử cuối cùng
    }
    const delay = Math.pow(2, i) * 1000; // Backoff lũy thừa: 1s, 2s, 4s...
    await new Promise(res => setTimeout(res, delay));
  }
};


const App = () => {
  const [selectedCoin, setSelectedCoin] = useState('bitcoin');
  const [selectedCoinDetails, setSelectedCoinDetails] = useState(null); // State mới để lưu chi tiết coin đã chọn
  const [predictionResult, setPredictionResult] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [historicalData, setHistoricalData] = useState([]);
  const [apiHistoricalData, setApiHistoricalData] = useState([]);
  const [fetchError, setFetchError] = useState(null);
  const [language, setLanguage] = useState('vi'); // Ngôn ngữ mặc định là tiếng Việt
  const [allCoins, setAllCoins] = useState([]);
  const [filteredCoins, setFilteredCoins] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showScrollToTop, setShowScrollToTop] = useState(false); // State để kiểm soát nút cuộn lên đầu
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const t = translations[language];
  const appContainerRef = useRef(null);

  // Debounce thuật ngữ tìm kiếm để tránh lọc quá mức
  const debouncedSearchTerm = useDebounce(searchTerm, 300); // Trì hoãn 300ms

  // Logic để hiển thị/ẩn nút cuộn lên đầu trang
  useEffect(() => {
    const handleScroll = () => {
      // Show button if user scrolls more than 300px
      if (window.scrollY > 300) {
        setShowScrollToTop(true);
      } else {
        setShowScrollToTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };


  const fetchAllCoins = async () => {
    setIsLoading(true);
    setPredictionResult(t.loadingCoins);
    console.log("Attempting to fetch all coins...");
    try {
      const response = await fetchWithRetry('https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&per_page=250&page=1');
      const data = await response.json();
      setAllCoins(data);
      // Khởi tạo các coin đã lọc với một tập hợp mặc định để hiển thị ban đầu
      setFilteredCoins(data.slice(0, 50));
      setPredictionResult(t.readyForPrediction);

      // Đặt chi tiết coin đã chọn ban đầu
      const initialCoin = data.find(coin => coin.id === 'bitcoin');
      if (initialCoin) {
        setSelectedCoinDetails(initialCoin);
      }
      console.log("Successfully fetched all coins.");
    } catch (error) {
      console.error("Error fetching coin list:", error);
      setPredictionResult(t.dataLoadingError);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCoinHistoricalData = async (coinId) => {
    setIsLoading(true);
    setFetchError(null);
    setPredictionResult(t.loadingData);
    console.log(`Attempting to fetch historical data for coin: ${coinId}`);
    try {
      const response = await fetchWithRetry(`https://api.coingecko.com/api/v3/coins/${coinId}/market_chart?vs_currency=usd&days=30`);
      const data = await response.json();
      const formattedData = data.prices.map(item => ({
        date: new Date(item[0]).toLocaleDateString(language),
        price: parseFloat(item[1])
      }));
      setApiHistoricalData(formattedData);
      setHistoricalData(formattedData);
      setPredictionResult(t.readyForPrediction);
      console.log(`Successfully fetched historical data for coin: ${coinId}`);
    } catch (error) {
      console.error("Error fetching historical data:", error);
      setFetchError(t.dataLoadingError);
      setApiHistoricalData([]);
      setHistoricalData([]);
      setPredictionResult(t.dataLoadingError);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAllCoins();
  }, []);

  useEffect(() => {
    if (selectedCoin) {
      fetchCoinHistoricalData(selectedCoin);
    }
    setPredictionResult(t.noPrediction);
  }, [selectedCoin, language]);

  // Sử dụng thuật ngữ tìm kiếm đã debounce để lọc coin
  useEffect(() => {
    if (debouncedSearchTerm === '') {
      // Hiển thị 50 coin hàng đầu nếu thuật ngữ tìm kiếm trống
      setFilteredCoins(allCoins.slice(0, 50));
    } else {
      const lowercasedSearchTerm = debouncedSearchTerm.toLowerCase();
      const filtered = allCoins.filter(coin =>
        coin.name.toLowerCase().includes(lowercasedSearchTerm) ||
        (coin.symbol && coin.symbol.toLowerCase().includes(lowercasedSearchTerm))
      );
      setFilteredCoins(filtered);
    }
  }, [debouncedSearchTerm, allCoins]);

  // Đóng dropdown khi click ra ngoài
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [searchRef]);


  const simulatePrediction = async () => {
    setIsLoading(true);
    setPredictionResult(t.loadingButtonText);

    if (apiHistoricalData.length === 0) {
      setPredictionResult(t.noDataForPrediction);
      setIsLoading(false);
      return;
    }

    setTimeout(() => {
      if (apiHistoricalData.length < 7) {
        setPredictionResult(t.notEnoughData);
        setIsLoading(false);
        return;
      }

      const prices = apiHistoricalData.map(d => d.price);
      const last7Prices = prices.slice(-7);
      const last3Prices = prices.slice(-3);
      const lastPrice = prices[prices.length - 1];

      const sumLast7 = last7Prices.reduce((acc, curr) => acc + curr, 0);
      const avgLast7 = sumLast7 / last7Prices.length;

      let predictionText = '';
      let simulatedPredictedPrice = lastPrice;

      const trendFactor = (last3Prices[2] - last3Prices[0]) / last3Prices[0];

      if (lastPrice > avgLast7 * 1.01 && trendFactor > 0.005) {
        predictionText = t.predictionStronglyUp;
        simulatedPredictedPrice = lastPrice * (1 + Math.random() * 0.03 + 0.01);
      } else if (lastPrice < avgLast7 * 0.99 && trendFactor < -0.005) {
        predictionText = t.predictionStronglyDown;
        simulatedPredictedPrice = lastPrice * (1 - Math.random() * 0.03 - 0.01);
      } else if (lastPrice > avgLast7 * 1.002) {
        predictionText = t.predictionSlightlyUp;
        simulatedPredictedPrice = lastPrice * (1 + Math.random() * 0.01);
      } else if (lastPrice < avgLast7 * 0.998) {
        predictionText = t.predictionSlightlyDown;
        simulatedPredictedPrice = lastPrice * (1 - Math.random() * 0.01);
      }
      else {
        predictionText = t.predictionStable;
        simulatedPredictedPrice = lastPrice + (Math.random() - 0.5) * lastPrice * 0.005;
      }

      simulatedPredictedPrice = Math.max(0, simulatedPredictedPrice);

      setPredictionResult(`${predictionText} ${t.predictionText(formatPrice(simulatedPredictedPrice))}`);

      const lastDate = new Date(apiHistoricalData[apiHistoricalData.length - 1].date);
      const nextDay = new Date(lastDate);
      nextDay.setDate(lastDate.getDate() + 1);

      const predictionPoint = {
        date: nextDay.toLocaleDateString(language),
        price: simulatedPredictedPrice,
        isPrediction: true
      };

      setHistoricalData([...apiHistoricalData, predictionPoint]);
      setIsLoading(false);
    }, 1500);
  };

  const handleCoinSelect = (coin) => {
    setSelectedCoin(coin.id);
    setSelectedCoinDetails(coin);
    setSearchTerm(''); // Xóa nội dung tìm kiếm để chuẩn bị cho lần tìm kiếm mới
    setIsDropdownOpen(false);
  };

  const handleSearchClick = () => {
    // Mở dropdown và focus vào input khi click vào khu vực tìm kiếm
    setIsDropdownOpen(true);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-start p-4 font-inter" ref={appContainerRef}>
      <div className="bg-white p-8 rounded-xl shadow-lg w-full max-w-3xl my-8">
        <div className="flex justify-end mb-4 items-center">
          <label htmlFor="language-select" className="block text-gray-700 text-sm font-semibold mr-2">
            {t.selectLanguageLabel}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <Globe className="h-5 w-5 text-gray-400" />
            </div>
            <select
              id="language-select"
              className="p-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200 appearance-none"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              disabled={isLoading}
            >
              <option value="vi">Tiếng Việt</option>
              <option value="en">English</option>
              <option value="zh">中文 (Chinese)</option>
              <option value="ja">日本語 (Japanese)</option>
              <option value="ko">한국어 (Korean)</option>
            </select>
          </div>
        </div>
        <h1 className="text-3xl font-bold text-center text-gray-800 mb-6">
          {t.appTitle}
        </h1>

        <div className="mb-6" ref={searchRef}>
          <label htmlFor="coin-search" className="block text-gray-700 text-sm font-semibold mb-2">
            {t.selectCoinLabel}
          </label>
          <div className="relative">
            {/* The actual search input field */}
            <input
              id="coin-search"
              ref={inputRef}
              type="text"
              placeholder={t.searchCoinPlaceholder}
              value={selectedCoinDetails && !isDropdownOpen ? selectedCoinDetails.name + ' (' + selectedCoinDetails.symbol.toUpperCase() + ')' : searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setSelectedCoinDetails(null);
                setIsDropdownOpen(true);
              }}
              onFocus={() => setIsDropdownOpen(true)}
              className="w-full p-3 pl-10 pr-16 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
              disabled={isLoading || allCoins.length === 0}
            />

            {/* Left-side coin icon */}
            <div className="absolute inset-y-0 left-0 flex items-center pl-3">
              {selectedCoinDetails && (
                <img
                  src={selectedCoinDetails.image}
                  alt={`${selectedCoinDetails.name} icon`}
                  className="h-5 w-5 rounded-full"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = `https://placehold.co/20x20/e2e8f0/64748b?text=${selectedCoinDetails.symbol.charAt(0).toUpperCase()}`;
                  }}
                />
              )}
              {!selectedCoinDetails && (
                <Search className="h-5 w-5 text-gray-400" />
              )}
            </div>

            {/* Right-side buttons */}
            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
              {selectedCoinDetails && (
                <button
                  type="button"
                  className="p-1 text-gray-400 hover:text-gray-600 transition duration-150 mr-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedCoinDetails(null);
                    setSelectedCoin('');
                    setHistoricalData([]);
                    setSearchTerm('');
                    setPredictionResult(t.readyForPrediction);
                    setIsDropdownOpen(false);
                  }}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
              <button
                type="button"
                className="p-1 text-gray-400 hover:text-gray-600 transition duration-150"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                disabled={isLoading || allCoins.length === 0}
              >
                <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${isDropdownOpen ? 'transform rotate-180' : ''}`} />
              </button>
            </div>
          </div>
          {isDropdownOpen && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {filteredCoins.length > 0 ? (
                filteredCoins.map(coin => (
                  <div
                    key={coin.id}
                    className={`p-3 flex items-center cursor-pointer hover:bg-blue-100 transition duration-150 ease-in-out ${selectedCoin === coin.id ? 'bg-blue-50 font-semibold' : ''}`}
                    onClick={() => handleCoinSelect(coin)}
                  >
                    <img
                      src={coin.image}
                      alt={`${coin.name} icon`}
                      className="h-5 w-5 rounded-full mr-2"
                      onError={(e) => {
                        e.target.onerror = null; // Ngăn chặn vòng lặp vô hạn
                        e.target.src = `https://placehold.co/20x20/e2e8f0/64748b?text=${coin.symbol.charAt(0).toUpperCase()}`;
                      }}
                    />
                    {coin.name} ({coin.symbol.toUpperCase()})
                  </div>
                ))
              ) : (
                <div className="p-3 text-gray-500">
                  {t.noCoinsFound}
                </div>
              )}
            </div>
          )}
        </div>

        <button
          onClick={simulatePrediction}
          className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-75"
          disabled={isLoading || apiHistoricalData.length === 0}
        >
          {isLoading ? t.loadingButtonText : t.predictButtonText}
        </button>

        {isLoading && (
          <div className="mt-6 text-center text-blue-600 font-medium">
            <div className="animate-spin inline-block w-6 h-6 border-4 border-blue-300 border-t-blue-600 rounded-full"></div>
            <p className="mt-2">{t.loadingMessage}</p>
          </div>
        )}

        {fetchError && (
          <div className="mt-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            <p>{fetchError}</p>
          </div>
        )}

        <div className="mt-8 p-6 bg-blue-50 rounded-lg border border-blue-200">
          <h2 className="text-xl font-bold text-blue-800 mb-4">{t.predictionTitle}</h2>
          <p className="text-lg text-blue-700 font-semibold">{predictionResult}</p>
        </div>

        {historicalData.length > 0 && (
          <div className="mt-8">
            <h2 className="text-xl font-bold text-gray-800 mb-4 text-center">
              {t.chartTitle}
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart
                data={historicalData}
                margin={{ top: 5, right: 30, left: 50, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis
                  domain={['dataMin * 0.95', 'dataMax * 1.05']}
                  tickFormatter={value => formatPrice(value)}
                />
                <Tooltip
                  formatter={(value) => [`${t.priceLabel}: ${formatPrice(value)}`]}
                  labelFormatter={(label) => `${t.dateLabel}: ${label}`}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="price"
                  stroke="#8884d8"
                  strokeWidth={2}
                  dot={(props) => {
                    const { cx, cy, payload, index } = props;
                    if (payload.isPrediction) {
                      return <circle key={index} cx={cx} cy={cy} r={6} fill="#ff7300" stroke="#ff7300" strokeWidth={2} />;
                    }
                    return <circle key={index} cx={cx} cy={cy} r={3} fill="#8884d8" />;
                  }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {historicalData.length > 0 && (
          <div className="mt-8">
            <h2 className="text-xl font-bold text-gray-800 mb-4 text-center">
              {t.tableTitle}
            </h2>
            <div className="bg-gray-50 p-4 rounded-lg shadow-inner overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider rounded-tl-lg">
                      {t.tableDateHeader}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider rounded-tr-lg">
                      {t.tablePriceHeader}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {historicalData.map((dataPoint, index) => (
                    <tr key={index} className={dataPoint.isPrediction ? 'bg-yellow-50 font-semibold' : ''}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {dataPoint.date} {dataPoint.isPrediction && t.predictionLabel}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {formatPrice(dataPoint.price)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Disclaimer section added here */}
        <div className="mt-8 p-6 bg-gray-200 rounded-lg border border-gray-300 text-gray-700">
          <h3 className="text-lg font-bold mb-2">{t.disclaimerTitle}</h3>
          {t.disclaimerContent.map((paragraph, index) => (
            <p key={index} className="text-sm mb-2">{paragraph}</p>
          ))}
        </div>
      </div>

      {/* Nút cuộn lên đầu trang */}
      {showScrollToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 transition-all duration-300 ease-in-out transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          aria-label="Cuộn lên đầu trang"
        >
          <ArrowUpCircle className="w-6 h-6" />
        </button>
      )}
    </div>
  );
};

export default App;
